// /diaries/:id/edit — the edit screen, as a **route**.
//
// W45.0 `ADR-0111` §P4. This used to be a `useState` flag inside
// `/diaries/:id`, and that choice is what produced the defect.
//
// `DiaryEditor` renders a `PageBottomActionBar`, and that bar's
// `pillNavHidden` branch depends on the *page* having claimed the bottom edge.
// `DiaryDetail` never called `useHidePillNav()`, so the bar took the
// non-claim branch — `PageBottomActionBar.tsx:135`,
// `bottom-[calc(env(safe-area-inset-bottom)+72px)]` — and landed **on top of** the
// pill rather than replacing it. Two rows of controls in one strip.
//
// The naive fix is to add the claim to `DiaryDetail`. It does not work: the
// claim is **route-scoped, not state-scoped** (`BottomChrome.tsx:73-74` is
// explicit that `useHidePillNav` must be called unconditionally), so the pill
// would also be hidden while *reading* a diary — and reading is a list-page
// activity that should keep its navigation.
//
// A route is the correct shape, and it is the one `/diaries/new` already uses.
// It also makes the reviewer's phrasing real: 「所有 edit Page」 stops being a
// category with one exception in it.
//
// The `+72px` lift that made the bug invisible is the thing W35.6 introduced
// this mechanism to delete — `BottomChrome.tsx:6-8` describes it as the old
// defect, and `PageBottomActionBar.tsx:127-132` keeps it as a "safety net". It
// is why a page that forgets to claim still *looks* handled.
//
// W54.0 A1 — the editor is **not mounted until the diary has loaded**.
//
// The editor seeds its fields once, at mount: `useState(initial?.date ??
// todayISO())`, `useState(initial?.is_private ?? false)`. On a cold load the
// query has not resolved, so `initial` was `undefined` and the editor showed
// **today's date, a public diary, an empty title and an empty body** — the
// original diary never appeared at all. Saving from that form PATCHed
// `date=today` and `is_private=false` onto the real diary: the date was
// rewritten and a private diary became public.
//
// Seeding is a one-shot, so no later fix could repair it — the value was
// already wrong by the time the data arrived. The route is therefore the only
// place the guard can live: the editor must not be mounted at all until the
// record is in hand. The same shape as `routes/anniversaries/[id]/edit.tsx`.

import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PageHero } from "@/components/primitives/PageHero";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { DiaryEditor } from "@/components/diary/DiaryEditor";
import { useToast } from "@/components/ui/toast";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { readDiary, updateDiary } from "@/lib/api/diary";
import { ApiError } from "@/lib/api/pair";
import { queryKeys } from "@/lib/queries/keys";
import { diaryErrorMessage } from "@/lib/i18n/diary";
import { INFO_BANNER_DIARY_LOAD_FAILED } from "@/lib/i18n/errors";
import { clearLegacyDiaryDrafts } from "@/lib/diary/drafts";
import type { DiaryUpdateInput } from "@/lib/api/diary";

export function DiaryEditPage() {
  // Unconditional, and the reason is the route: this screen owns the bottom
  // edge for as long as it is mounted, and leaving it releases the claim.
  useHidePillNav();

  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();

  const diaryQuery = useQuery({
    queryKey: queryKeys.diary.detail(id),
    queryFn: () => readDiary(id),
    enabled: id.length > 0,
  });

  // W54.0 A1 — the unscoped edit-mode drafts (`diary-title:edit` /
  // `diary-content:edit`) are invalidated, not migrated: they were never
  // attributable to a diary, and surfacing one in whichever diary happens to
  // be open is the defect the new per-diary keys remove. Deliberate, and
  // recorded in the spec rather than done quietly.
  useEffect(() => {
    clearLegacyDiaryDrafts();
  }, []);

  const updateMut = useMutation({
    mutationFn: (payload: DiaryUpdateInput) => updateDiary(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.diary.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      pushToast({ variant: "success", message: "日記已更新" });
      // Back to the record, not to the list — the same reasoning the calendar
      // event detail's breadcrumb carries: 「up」 is the thing you were reading.
      nav(`/diaries/${id}`, { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        pushToast({
          variant: "danger",
          message: diaryErrorMessage(err.code, err.message),
        });
      }
    },
  });

  const onSubmit = useCallback(
    async (payload: DiaryUpdateInput) => {
      await updateMut.mutateAsync(payload);
    },
    [updateMut],
  );

  return (
    <PageErrorBoundary label="編輯日記">
      <div data-testid="diary-edit-page" className="flex flex-col">
        {/* W54.0 A1 — three mutually exclusive states, and the editor belongs to
            exactly one of them. Loading and error both mean **the record is not
            in hand**, and an editor seeded from a record that is not in hand
            writes today's date over a private diary. So the editor is not
            rendered in either branch — it is not in the DOM at all.

            `isError || !data` rather than `isError` alone: a read that failed
            leaves `data` undefined, and so does a query that never ran (no id
            in the URL). Both are "the record is not in hand", and the editor
            must not be mounted for either. */}
        {diaryQuery.isLoading ? (
          <LoadingState count={3} shape="card" />
        ) : diaryQuery.isError || !diaryQuery.data ? (
          <ErrorState
            description={INFO_BANNER_DIARY_LOAD_FAILED}
            onRetry={() => void diaryQuery.refetch()}
          />
        ) : (
          <>
            {/* W50.0 §6.2 — the hero used to sit **inside** a `PageContainer` of its
                own, which is the double-gutter defect W49.0 §6 fixed on the wishlist
                pages: `PageHero` carries its own `px-s-4 md:px-s-6` and `PageContainer`
                carries the identical gutter, so nesting them doubled the horizontal
                offset and added 20px of `py-s-5` above the title. It survived because
                the hero-order guard hard-coded five wishlist filenames; deriving the
                set from `App.tsx` found it on the first run.
                Same shape as `EventForm.tsx`: hero and container siblings, hero first. */}
            <PageHero
              // W45.0 `ADR-0115`: the same trail the read view has, so 「where am
              // I」 does not change when you cross into editing.
              crumbs={[
                { to: "/diaries", label: "日記" },
                { label: "編輯" },
              ]}
              title={diaryQuery.data?.title || "編輯日記"}
              subtitle={diaryQuery.data?.date}
            />

            {/* W51 (D-5) — the `PageContainer` wrapper is gone. `DiaryEditor` owns
                this shape (its own container, section stack, dock bar and
                clearance); this page was nesting a second copy of it, which cost
                32px of gutter instead of 16px and is the same defect W50.0 §6.2
                fixed for the hero one level up. */}

            {/* `key` — the editor seeds its state once, at mount. Moving from one
                diary to another without remounting it would carry the first
                diary's date, privacy and draft into the second. The draft keys
                are scoped by id (W54.0 A1), and the state has to be. */}
            <DiaryEditor
              key={id}
              mode="edit"
              diaryId={id}
              initial={diaryQuery.data}
              busy={updateMut.isPending}
              onSubmit={onSubmit}
            />
          </>
        )}
      </div>
    </PageErrorBoundary>
  );
}
